import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, AlertTriangle, RefreshCw, Save, X, Pill } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import PrescriptionMedicineSection from './PrescriptionMedicineSection';

export const PrescriptionMedicineEditModal = ({
  open,
  onOpenChange,
  prescription,
  onSuccess,
  onConflict
}) => {
  const [medicines, setMedicines] = useState([]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflictError, setConflictError] = useState(null);
  const [showZeroConfirm, setShowZeroConfirm] = useState(false);

  useEffect(() => {
    if (open && prescription) {
      setConflictError(null);
      setReason('');
      setShowZeroConfirm(false);

      const initialMeds = Array.isArray(prescription.medicines)
        ? prescription.medicines.map((m) => ({
          name: m.name || '',
          dosage: m.dosage || '',
          instructions: m.instructions || '',
          frequency: m.frequency || '',
          duration: m.duration || '',
          erp_product_id: m.erp_product_id || null,
          dispensed_quantity:
            m.dispensed_quantity !== undefined && m.dispensed_quantity !== null
              ? Number(m.dispensed_quantity)
              : 1,
          dispensed_unit: m.dispensed_unit || 'UNIT',
          available_stock: m.available_stock !== undefined ? m.available_stock : null,
          is_manual: !m.erp_product_id
        }))
        : [];
      setMedicines(initialMeds);
    }
  }, [open, prescription]);

  const handleSave = async (forceZero = false) => {
    if (medicines.length === 0 && !forceZero) {
      setShowZeroConfirm(true);
      return;
    }

    // Validate medicines
    for (let i = 0; i < medicines.length; i++) {
      const m = medicines[i];
      if (!m.name || !m.name.trim()) {
        toast.error(`Medicine #${i + 1} requires a valid name. Please select or enter a name, or remove the line.`);
        return;
      }
      const qty = Number(m.dispensed_quantity);
      if (isNaN(qty) || qty <= 0) {
        toast.error(`Quantity for "${m.name}" must be greater than 0.`);
        return;
      }
    }

    setSaving(true);
    setConflictError(null);

    const payload = {
      expected_version: prescription?.version || 1,
      medicines: medicines.map((m) => ({
        name: m.name.trim(),
        dosage: (m.dosage || '').trim(),
        instructions: (m.instructions || '').trim(),
        frequency: (m.frequency || '').trim(),
        duration: (m.duration || '').trim(),
        erp_product_id: m.erp_product_id ? Number(m.erp_product_id) : null,
        dispensed_quantity: Number(m.dispensed_quantity) > 0 ? Number(m.dispensed_quantity) : 1,
        dispensed_unit: m.dispensed_unit || 'UNIT'
      })),
      reason: reason.trim() || undefined
    };

    try {
      const res = await api.put(`/prescriptions/${prescription.id}/medicines`, payload);
      const data = res.data?.data || res.data;
      toast.success('Prescription medicines updated successfully');
      setShowZeroConfirm(false);
      onSuccess?.(data);
      onOpenChange(false);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.message || err.response?.data?.detail || 'Failed to update medicines';

      if (status === 409) {
        setConflictError(detail);
        toast.error('Conflict: This prescription was modified by another user. Please reload the latest version.');
      } else if (status === 400 && detail.includes('immutable')) {
        toast.error('This prescription has been completed or cancelled and can no longer be edited.');
        onOpenChange(false);
        onConflict?.();
      } else {
        toast.error(detail);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReload = () => {
    onConflict?.();
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[96vw] sm:max-w-4xl max-h-[92vh] flex flex-col p-0 overflow-hidden rounded-xl shadow-2xl">
          <DialogHeader className="p-4 sm:p-5 border-b border-border bg-card shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-200 shrink-0">
                <Pill className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <DialogTitle className="font-serif text-lg sm:text-xl font-bold text-foreground">
                    Edit Medicines — Prescription #{prescription?.id}
                  </DialogTitle>
                  {prescription?.version && (
                    <span className="text-xs font-mono bg-muted text-muted-foreground px-2 py-0.5 rounded font-medium">
                      v{prescription.version}
                    </span>
                  )}
                </div>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                  Modify prescribed medicines, dosage regimens, and quantities prior to dispensing completion.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
            {conflictError && (
              <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-start gap-3">
                <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                <div className="flex-1 space-y-2">
                  <p className="font-semibold">Concurrency Conflict (OCC)</p>
                  <p className="text-xs">{conflictError}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleReload}
                    className="gap-1.5 text-xs h-7 border-destructive/30 hover:bg-destructive/10"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Reload Latest Version
                  </Button>
                </div>
              </div>
            )}

            {medicines.length === 0 && (
              <div className="p-3.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-900 dark:text-amber-200 text-xs flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <span>
                  <strong>Zero medicines:</strong> Saving with 0 medicines will mark this as a pure clinical consultation with no pharmacy dispensing.
                </span>
              </div>
            )}

            <PrescriptionMedicineSection
              medicines={medicines}
              onChange={setMedicines}
              optional={true}
              title="Prescribed Medicine Lines"
              inModal={true}
            />

            <div className="space-y-1.5 pt-3 border-t border-border">
              <Label htmlFor="medicine-edit-reason" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Reason for Change <span className="text-muted-foreground font-normal">(Optional)</span>
              </Label>
              <Textarea
                id="medicine-edit-reason"
                placeholder="e.g., Patient reported stomach sensitivity; substituted churna with tablet formulation."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="text-xs min-h-[64px] resize-y"
                maxLength={500}
              />
            </div>
          </div>

          <DialogFooter className="p-3.5 sm:p-4 border-t border-border bg-muted/20 shrink-0 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              className="w-full sm:w-auto h-9 text-xs sm:text-sm"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => handleSave(false)}
              disabled={saving || Boolean(conflictError)}
              className="w-full sm:w-auto h-9 gap-1.5 bg-emerald-800 hover:bg-emerald-900 text-white text-xs sm:text-sm font-medium"
            >
              {saving ? (
                <>
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  Saving Medicines...
                </>
              ) : (
                <>
                  <Save className="h-3.5 w-3.5" />
                  Save Medicines
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Zero Medicines */}
      <Dialog open={showZeroConfirm} onOpenChange={setShowZeroConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              <DialogTitle className="text-base font-semibold">Confirm Zero Medicines</DialogTitle>
            </div>
            <DialogDescription className="text-xs text-muted-foreground pt-1">
              You have removed all medicines from this prescription. Saving will convert this prescription into a
              clinical-only consultation. No inventory will be dispensed, and no ERP sale will be generated upon completion.
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 bg-muted/40 rounded-lg text-xs text-slate-700 dark:text-slate-300">
            Are you sure you want to proceed with <strong>0 medicines</strong>?
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowZeroConfirm(false)}
              disabled={saving}
            >
              Keep Editing
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => handleSave(true)}
              disabled={saving}
              className="gap-1.5"
            >
              {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Yes, Save 0 Medicines
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PrescriptionMedicineEditModal;

